/**
 * MY CLASSROOMS (A4) — the student's landing: one card per cohort they're
 * enrolled in, with their progress and the door to their classroom.
 *
 * Until now `/academy/cohorts` was the only cohorts screen and only the
 * teacher tier saw it: a student had no surface for their classroom at
 * all even though the data was already on their device. This doesn't open any
 * new source — it lists the student's own enrollments that already sync.
 */
import { type Component, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { GraduationCap, School } from "@fleet/icons";
import { Link } from "@tanstack/solid-router";
import { stableRows } from "@/lib/solid/stable-rows";
import { useI18n } from "@/providers/i18n-provider";
import { useSession } from "@/lib/auth";
import { PageContainer, PageHeader, Skeleton } from "@fleet/ui";
import { EmptyState } from "@/components/ui/empty-state";
import { HelpButton } from "@/features/help/components/help-button";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";
import { CompletionProgress } from "../../completion/completion-progress";
import { useUserEnrollments } from "../../hooks/use-people-data";
import { useCohortChatUnread } from "../../hooks/use-classroom-chat";

/**
 * B2 · unread-messages bubble for a classroom. Calculated over what's already
 * synced —there's no endpoint to ask— by comparing against MY read
 * marker, which does travel across devices.
 */
const UnreadBubble: Component<{ cohortId: string }> = (props) => {
  const unread = useCohortChatUnread(() => props.cohortId);
  return (
    <Show when={unread() > 0}>
      <span
        class="rounded-full bg-accent px-2 py-0.5 text-xs font-bold text-accent-foreground"
        data-testid="classroom-card-unread"
      >
        {unread()}
      </span>
    </Show>
  );
};

/** Enrollments that grant access to a classroom (a withdrawal has no classroom to open). */
const OPEN_STATUSES = new Set(["active", "completed"]);

export const MyClassroomsView: Component = () => {
  const { t } = useI18n();
  const { userId } = useSession();
  const enrollments = useUserEnrollments(userId);
  const chatFlag = useFeatureEnabled("academy.classroom.chat");

  const rooms = stableRows(
    () => (enrollments.data ?? []).filter((e) => OPEN_STATUSES.has(e.status ?? "") && e.cohortId),
    { ignore: ["raw"] },
  );

  return (
    <PageContainer maxWidth="3xl">
      <div class="space-y-5">
        <div class="flex items-start justify-between gap-2">
          <PageHeader
            title={t(msg`My classrooms`)}
            description={t(msg`Your cohorts: announcements, class plan and progress.`)}
            icon={School}
          />
          <HelpButton area="classroom" />
        </div>

        <Show
          when={!enrollments.isLoading || rooms().length > 0}
          fallback={<Skeleton class="h-32 w-full rounded-2xl" />}
        >
          <Show
            when={rooms().length > 0}
            fallback={
              <EmptyState
                icon={GraduationCap}
                title={t(msg`You have no classroom yet`)}
                description={t(
                  msg`When the academy enrolls you in a cohort, its classroom shows up here.`,
                )}
              />
            }
          >
            <ul class="space-y-3" data-testid="my-classrooms">
              <For each={rooms()}>
                {(e) => (
                  <li>
                    <Link
                      to="/classroom/$cohortId"
                      params={{ cohortId: e.cohortId ?? "" }}
                      class="block space-y-2 rounded-2xl border border-border p-4 transition-colors hover:border-primary/50"
                      data-testid="classroom-card"
                      data-cohort-id={e.cohortId ?? ""}
                    >
                      <div class="flex flex-wrap items-center justify-between gap-2">
                        <p class="font-display text-lg font-bold">
                          {e.cohortName ?? e.cohortCode ?? e.cohortId}
                        </p>
                        <span class="flex items-center gap-2">
                          <Show when={chatFlag().enabled && e.cohortId}>
                            {(cid) => <UnreadBubble cohortId={cid()} />}
                          </Show>
                          <span class="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                            {e.status}
                          </span>
                        </span>
                      </div>
                      <Show when={e.cohortId}>
                        {(cid) => <CompletionProgress cohortId={cid()} />}
                      </Show>
                    </Link>
                  </li>
                )}
              </For>
            </ul>
          </Show>
        </Show>
      </div>
    </PageContainer>
  );
};
